import { FC } from "react";
import { ChevronDownIcon, ChevronUpIcon, MinusIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/shadcn/utils";

interface PercentageChangeTextProps {
    value: number;
    description?: string;
    valueFormatter?: (value: number) => string;
    extraClasses?: string;
}

const PercentageChangeText: FC<PercentageChangeTextProps> = ({
    value,
    description = "",
    valueFormatter,
    extraClasses,
}) => {
    const formatter = valueFormatter ? valueFormatter : (value: number) => value;

    // Defence in depth: a non-finite value (Infinity/-Infinity/NaN — e.g. a
    // division by a zero baseline upstream) is treated as the flat state rather
    // than rendered literally as "Infinity%"/"NaN%".
    const safeValue = Number.isFinite(value) ? value : 0;

    const getColorClasses = (value: number) => {
        // green-600/red-600, NOT the -500s — both -500s on white fail WCAG AA (4.5:1):
        // green-500 is ~2.28:1, red-500 is ~3.9:1.
        if (value > 0) return "text-green-700";
        if (value < 0) return "text-red-600";
        return "text-gray-700";
    };

    const colorClasses = getColorClasses(safeValue);

    const renderIcon = () => {
        const iconClasses = cn("h-3 w-3", colorClasses);
        if (safeValue > 0) return <ChevronUpIcon className={iconClasses} />;
        if (safeValue < 0) return <ChevronDownIcon className={iconClasses} />;
        return <MinusIcon className={iconClasses} />;
    };

    return (
        <div className="flex items-center justify-end gap-0.5">
            {renderIcon()}
            {safeValue !== 0 && (
                <span className={cn("text-xs", colorClasses, extraClasses)}>{formatter(Math.abs(safeValue))}%</span>
            )}
            {description && <span className="ml-2 text-xs text-muted-foreground">{description}</span>}
        </div>
    );
};

export default PercentageChangeText;
